import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router';

const HIDFileUploader = () => {
  const { key, id } = useParams();
  const [device, setDevice] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState('Not connected');
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [logs, setLogs] = useState([]);
  const [logsVisible, setLogsVisible] = useState(true);
  const [zipBlob, setZipBlob] = useState(null);
  const [fileType, setFileType] = useState(null); // Track if it's JSON or ZIP
  const [isLoading, setIsLoading] = useState(false);
  const [uploadDisabled, setUploadDisabled] = useState(true);
  const [connectDisabled, setConnectDisabled] = useState(false);
  const [campaignNotFound, setCampaignNotFound] = useState(false);
  const [downloadAttempted, setDownloadAttempted] = useState(false); // Track download attempts

  const logContainerRef = useRef(null);

  // HID Protocol constants
  const REPORT_LEN = 65;
  let gNumber = 0x01;

  const log = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `${timestamp}: ${message}`;
    setLogs((prev) => [...prev, logEntry]);
    setTimeout(() => {
      if (logContainerRef.current) {
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      }
    }, 0);
  };

  const getCampaignId = () => {
    return id || 'Unknown';
  };

  const bufferToHexString = (buffer) => {
    return Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0').toUpperCase())
      .join(' ');
  };

  // LRC (Longitudinal Redundancy Check) functions
  const lrc = (data) => {
    let cLRC = 0;
    for (let i = 0; i < data.length; i++) {
      cLRC ^= data[i];
    }
    return cLRC;
  };

  const LRC = (cLRC, data) => {
    for (let i = 0; i < data.length; i++) {
      cLRC ^= data[i];
    }
    return cLRC;
  };

  // Send command with data to device
  const cs_send = async (cmd, data, dataLen, outLRC) => {
    if (!device) { 
      log('Device not connected'); 
      return -1; 
    }

    const cmdData = new Uint8Array(1024 * 10);
    cmdData[2] = 0x02;
    cmdData[3] = gNumber;
    cmdData[4] = cmd[0];
    cmdData[5] = cmd[1];

    const bufflen = REPORT_LEN - 9;
    cmdData[6] = dataLen >> 8;
    cmdData[7] = dataLen & 0xFF;

    let reallen = dataLen;
    if (dataLen > 0) {
      if (dataLen > bufflen) {
        cmdData.set(data.subarray(0, bufflen + 1), 8);
        reallen = bufflen + 1;
      } else {
        cmdData.set(data.subarray(0, dataLen), 8);
      }
    }

    gNumber = (gNumber === 0xFF ? 0x01 : gNumber + 1);

    let length, cLRC;
    if (dataLen > bufflen) {
      length = 6 + reallen;
      cLRC = lrc(cmdData.subarray(2, 6 + reallen));
      outLRC[0] = cLRC;
    } else {
      cLRC = lrc(cmdData.subarray(2, 6 + reallen));
      cmdData[8 + reallen] = cLRC;
      length = 7 + reallen;
    }

    cmdData[0] = length >> 8;
    cmdData[1] = length & 0xFF;
    length = length + 2;

    if (length > REPORT_LEN) {
      log('❌ packet larger than report size');
      return -1;
    }

    console.log(`Sending: ${bufferToHexString(cmdData.slice(0, length))}`);
    
    try {
      await device.sendReport(0, cmdData.slice(1, length));
      return length;
    } catch (error) {
      log(`Send error: ${error.message}`);
      return -1;
    }
  };

  // Read data from device with timeout
  const cs_read = (timeout_ms) => {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        resolve({ length: 0, data: new Uint8Array(0) });
      }, timeout_ms);

      const onInputReport = (event) => {
        clearTimeout(timeoutId);
        device.removeEventListener('inputreport', onInputReport);

        const data = new Uint8Array(event.data.buffer);
        console.log(`Received: ${bufferToHexString(data)}`);
        log(`Received: ${bufferToHexString(data)}`);

        if (data.length === 0) {
          resolve({ length: -99, data: new Uint8Array(0) });
          return;
        }

        const len_byte = data[0];
        if (len_byte < 7) {
          resolve({ length: -1, data: new Uint8Array(0) });
          return;
        }

        if (data[1] !== 0x02) {
          resolve({ length: -2, data: new Uint8Array(0) });
          return;
        }

        const cmdtype = data[3];
        const cmd = data[4];
        const datalen = (data[5] << 8) | data[6];

        if (datalen > 0 && datalen === len_byte - 7) {
          const resultData = new Uint8Array(datalen + 2);
          resultData[0] = cmdtype;
          resultData[1] = cmd;
          resultData.set(data.slice(7, 7 + datalen), 2);
          resolve({ length: datalen + 2, data: resultData });
        } else {
          resolve({ length: -3, data: new Uint8Array(0) });
        }
      };

      device.addEventListener('inputreport', onInputReport);
    });
  };

  // Check if command response is valid
  const IsIllegalCMD = (cmd, buff) => {
    console.log(`response cmd: ${bufferToHexString(buff)}`);
    if (buff[0] !== cmd[0] || buff[1] !== cmd[1]) {
      const err = `request cmd [${cmd[0].toString(16).padStart(2, '0').toUpperCase()}${cmd[1].toString(16).padStart(2, '0').toUpperCase()}] is not equal to response cmd [${buff[0].toString(16).padStart(2, '0').toUpperCase()}${buff[1].toString(16).padStart(2, '0').toUpperCase()}]`;
      return { valid: false, error: err };
    }

    if (buff[2] === 0x00 && buff[3] === 0x00) {
      return { valid: true, error: "" };
    }

    const err = `cmd return fail [${buff[2].toString(16).padStart(2, '0').toUpperCase()}${buff[3].toString(16).padStart(2, '0').toUpperCase()}]`;
    return { valid: false, error: err };
  };

  // Process and upload file
  const processFile = async (fileData, cmd) => {
    try {
      const filelen = fileData.length;
      let rlen = filelen;
      const bufflen = REPORT_LEN - 9 - 12;
      let startAddr = 0;

      log(`File size: ${filelen} bytes`);

      const pt = new Uint8Array(1024);

      while (rlen > 0) {
        const len = Math.min(rlen, 4 * 1024);
        const buff = fileData.slice(startAddr, startAddr + len);

        // Convert startAddr to big-endian bytes
        pt[0] = (startAddr >> 24) & 0xFF;
        pt[1] = (startAddr >> 16) & 0xFF;
        pt[2] = (startAddr >> 8) & 0xFF;
        pt[3] = startAddr & 0xFF;

        // Convert length to big-endian bytes
        pt[4] = (len >> 24) & 0xFF;
        pt[5] = (len >> 16) & 0xFF;
        pt[6] = (len >> 8) & 0xFF;
        pt[7] = len & 0xFF;

        // Convert filelen to big-endian bytes
        pt[8] = (filelen >> 24) & 0xFF;
        pt[9] = (filelen >> 16) & 0xFF;
        pt[10] = (filelen >> 8) & 0xFF;
        pt[11] = filelen & 0xFF;

        if (len <= bufflen) {
          pt.set(buff, 12);
          const datalen = 12 + len;
          const outLRC = new Uint8Array(1);
          const res = await cs_send(cmd, pt.slice(0, datalen), datalen, outLRC);
          if (res <= 0) {
            throw new Error("Failed to send data.");
          }
        } else {
          // Handle large chunks
          pt.set(buff.slice(0, bufflen + 1), 12);
          const datalen = 12 + len;
          const outLRC = new Uint8Array(1);
          const res = await cs_send(cmd, pt.slice(0, datalen), datalen, outLRC);
          if (res <= 0) {
            throw new Error("Failed to send data.");
          }

          let packlen = len;
          let pCur = bufflen + 1;
          packlen = packlen - bufflen - 1;
          let bSendLRC = false;
          let cLRC = 0;

          while (packlen > 0) {
            const curlen = Math.min(packlen, REPORT_LEN - 2);
            const tempBuff = buff.slice(pCur, pCur + curlen);

            cLRC = LRC(cLRC, tempBuff);

            if (curlen < REPORT_LEN - 2) {
              pt[0] = ((curlen + 1) >> 8) & 0xFF;
              pt[1] = (curlen + 1) & 0xFF;
              pt.set(tempBuff, 2);
              pt[2 + curlen] = cLRC;
              bSendLRC = true;
            } else {
              pt[0] = (curlen >> 8) & 0xFF;
              pt[1] = curlen & 0xFF;
              pt.set(tempBuff, 2);
            }

            const sendlen = bSendLRC ? 3 + curlen : 2 + curlen;
            log(`[SEND] ${bufferToHexString(pt.slice(0, sendlen))}`);
            await device.sendReport(0, pt.slice(1, sendlen));

            pCur = pCur + curlen;
            packlen = packlen - curlen;
          }

          if (!bSendLRC) {
            pt[0] = (1 >> 8) & 0xFF;
            pt[1] = 1 & 0xFF;
            pt[2] = cLRC;

            log(`Sending LRC: ${bufferToHexString(pt.slice(0, 3))}`);
            await device.sendReport(0, pt.slice(0, 3));
          }
        }

        const { length: ret, data: readBuff } = await cs_read(3000);
        if (ret <= 0) {
          throw new Error("Upload failed - Read timeout");
        }

        const { valid: iicValid, error: iicErr } = IsIllegalCMD(cmd, readBuff);
        if (!iicValid) {
          log(`Error: ${iicErr}`);
        }

        rlen = rlen - len;
        startAddr = startAddr + len;

        const progressPercent = Math.floor((startAddr / filelen) * 100);
        setProgress(progressPercent);
        log(`Progress: ${startAddr}/${filelen} bytes sent (${progressPercent}%)`);
      }

      log("File upload completed!");
      return true;
    } catch (error) {
      log(`Error: ${error.message}`);
      return false;
    }
  };

  // IMPROVED: Download ZIP file from API with better error handling
  const downloadZipFile = async (retryAttempt = false) => {
    // Prevent multiple downloads if already have a valid file
    if (zipBlob && zipBlob.size > 0 && !retryAttempt) {
      log('ZIP file already downloaded and cached.');
      return;
    }

    // Prevent multiple downloads if already attempted and failed
    if (downloadAttempted && !retryAttempt) {
      log('Download already attempted. Use retry button if needed.');
      return;
    }

    setIsLoading(true);
    log('Downloading ZIP file from API...');
    
    try {
      const response = await fetch(
        `https://api.dqr-display-tms.eventum.co.in/api/Share/DeviceJsonSetting/File/${key}/${id}`,
        {
          method: 'GET',
          headers: {
            accept: '*/*',
          },
        }
      );

      log(`API Response Status: ${response.status}`);
      log(`API Response Headers: ${JSON.stringify(Object.fromEntries(response.headers))}`);

      if (response.ok) {
        // Check content type to determine file type
        const contentType = response.headers.get('content-type');
        const contentDisposition = response.headers.get('content-disposition');
        log(`Content-Type: ${contentType}`);
        log(`Content-Disposition: ${contentDisposition}`);

        // Get content length to check file size
        const contentLength = response.headers.get('content-length');
        log(`Content-Length: ${contentLength} bytes`);

        if (contentLength && parseInt(contentLength) === 0) {
          throw new Error('Received empty file (0 bytes). The API key may have expired.');
        }

        const blob = await response.blob();
        log(`Blob size: ${blob.size} bytes`);
        log(`Blob type: ${blob.type}`);

        // Additional check for blob size
        if (blob.size === 0) {
          throw new Error('Downloaded file is empty (0 bytes). The API key may have expired or been used already.');
        }

        // Determine file type based on content-type and content-disposition
        let detectedFileType = 'unknown';
        if (contentType && contentType.includes('application/json')) {
          detectedFileType = 'json';
        } else if (contentType && (contentType.includes('zip') || contentType.includes('application/octet-stream'))) {
          detectedFileType = 'zip';
        } else if (contentDisposition && contentDisposition.includes('filename=setting.json')) {
          detectedFileType = 'json';
        }

        log(`Detected file type: ${detectedFileType}`);

        // Handle JSON response (setting.json)
        if (detectedFileType === 'json') {
          const text = await blob.text();
          log(`JSON content preview: ${text.substring(0, 200)}...`);
          
          try {
            const jsonData = JSON.parse(text);
            log('Valid JSON file received');
            setZipBlob(blob);
            setFileType('json');
            setDownloadAttempted(true);
            log('JSON file downloaded and cached for upload');
            setMessage('Settings file (JSON) downloaded and ready for upload');
            setMessageType('success');
          } catch (jsonError) {
            throw new Error('Invalid JSON file received');
          }
        } 
        // Handle ZIP response
        else if (detectedFileType === 'zip') {
          setZipBlob(blob);
          setFileType('zip');
          setDownloadAttempted(true);
          log('ZIP file downloaded and cached for upload');
          setMessage('ZIP file downloaded and ready for upload');
          setMessageType('success');
        }
        // Handle unknown file type
        else {
          // Try to read as text to see if it's an error message
          const text = await blob.text();
          log(`Response body (as text): ${text}`);
          
          try {
            const errorData = JSON.parse(text);
            if (errorData.errormsg) {
              throw new Error(errorData.errormsg);
            }
          } catch (jsonError) {
            // If not JSON error, treat as unknown file type
            log('Unknown file type received, treating as binary file');
            setZipBlob(blob);
            setFileType('unknown');
            setDownloadAttempted(true);
            setMessage('File downloaded (unknown type) - ready for upload');
            setMessageType('success');
          }
        }
      } else {
        // Handle different error cases
        let errorMessage = `HTTP error! status: ${response.status}`;
        
        try {
          const responseText = await response.text();
          log(`Error response body: ${responseText}`);
          
          const errorData = JSON.parse(responseText);
          if (errorData.errormsg && errorData.errormsg.toLowerCase().includes('campaign not found')) {
            log('Campaign not found - redirecting to not found page');
            setCampaignNotFound(true);
            return;
          }
          errorMessage = errorData.errormsg || errorMessage;
        } catch (jsonError) {
          // If response is not JSON, use the status error
          log('Error parsing response JSON, using status error');
        }
        
        setDownloadAttempted(true);
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (!campaignNotFound) {
        log(`Error downloading ZIP file: ${error.message}`);
        setMessage(`Failed to download ZIP file: ${error.message}`);
        setMessageType('error');
        setDownloadAttempted(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // NEW: Retry download function
  const handleRetryDownload = () => {
    setDownloadAttempted(false);
    setZipBlob(null);
    setFileType(null);
    setMessage('');
    setMessageType('');
    downloadZipFile(true);
  };

  // Manual download function
  const handleManualDownload = () => {
    if (!zipBlob) {
      log("No file available to download");
      return;
    }
    
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    
    // Set filename based on file type
    let filename = `campaign_${getCampaignId()}`;
    if (fileType === 'json') {
      filename += '.json';
    } else if (fileType === 'zip') {
      filename += '.zip';
    } else {
      filename += '.bin'; // Unknown file type
    }
    
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    log(`File download started: ${filename}`);
  };

  useEffect(() => {
    if (!('hid' in navigator)) {
      log('WebHID is not supported in this browser');
      setConnectDisabled(true);
    }
    
    // Auto-download ZIP file ONCE on component mount
    log('Component mounted - starting automatic ZIP download');
    downloadZipFile();
  }, []); // Empty dependency array ensures this runs only once

  const handleConnect = async () => {
    try {
      const devices = await navigator.hid.requestDevice({
        filters: [{ vendorId: 0x2C7C, productId: 0x0901 }],
      });

      if (devices.length === 0) {
        log('No device selected');
        return;
      }

      const selectedDevice = devices[0];
      await selectedDevice.open();
      setDevice(selectedDevice);
      setDeviceStatus(`Connected: ${selectedDevice.productName} PID 0x2C7C, VID 0x0901`);
      log(`Connected to ${selectedDevice.productName}`);
      setUploadDisabled(false);
      setConnectDisabled(true);
    } catch (error) {
      log(`Error connecting to device: ${error.message}`);
      setMessage(`Connection failed: ${error.message}`);
      setMessageType('error');
    }
  };

  const handleUpload = async () => {
    if (!device) {
      log('Please connect to a device first');
      setMessage('Please connect to a device first');
      setMessageType('error');
      return;
    }

    if (!zipBlob) {
      log('No ZIP file available. Please wait for download to complete or retry download.');
      setMessage('No ZIP file available. Please retry download.');
      setMessageType('error');
      return;
    }

    if (zipBlob.size === 0) {
      log('ZIP file is empty. Please retry download.');
      setMessage('ZIP file is empty. Please retry download.');
      setMessageType('error');
      return;
    }

    setUploadDisabled(true);
    setProgress(0);
    setMessage('');
    setMessageType('');

    try {
      log('Using cached file for upload (no additional API call)');
      log(`File type: ${fileType}, Size: ${zipBlob.size} bytes`);
      
      // Convert blob to ArrayBuffer then to Uint8Array
      const arrayBuffer = await zipBlob.arrayBuffer();
      const fileData = new Uint8Array(arrayBuffer);
      
      // Command for file upload
      const cmd = new Uint8Array([0x03, 0x15]);
      
      const fileTypeDisplay = fileType === 'json' ? 'JSON' : fileType === 'zip' ? 'ZIP' : 'Binary';
      log(`Starting upload of cached ${fileTypeDisplay} file (${Math.round(zipBlob.size / 1024)} KB)`);
      const success = await processFile(fileData, cmd);
      
      if (success) {
        setMessage("Upload completed successfully!");
        setMessageType("success");
        log("Upload completed successfully!");
      } else {
        setMessage("Upload failed!");
        setMessageType("error");
        log("Upload failed!");
      }
    } catch (error) {
      log(`Upload error: ${error.message}`);
      setMessage(`Upload failed: ${error.message}`);
      setMessageType("error");
    } finally {
      setUploadDisabled(false);
    }
  };

  // Not Found Component
  const NotFoundPage = () => (
    <div style={{
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      margin: 0,
      padding: '20px'
    }}>
      <div style={{
        background: '#ffffffee',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
        borderRadius: '15px',
        width: '450px',
        maxWidth: '90vw',
        padding: '50px',
        textAlign: 'center',
        border: '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{
          fontSize: '72px',
          marginBottom: '20px'
        }}>
          🔍
        </div>
        <h1 style={{
          color: '#e74c3c',
          fontSize: '32px',
          fontWeight: '700',
          marginBottom: '20px'
        }}>
          Campaign Not Found
        </h1>
        <p style={{
          color: '#666',
          fontSize: '18px',
          marginBottom: '30px',
          lineHeight: '1.6'
        }}>
          The campaign you're looking for doesn't exist or has been removed.
        </p>
        <div style={{
          background: '#f8f9fa',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '30px',
          border: '1px solid #dee2e6'
        }}>
          <p style={{
            color: '#495057',
            fontSize: '14px',
            margin: 0
          }}>
            Campaign ID: <strong>{getCampaignId()}</strong>
          </p>
        </div>
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => window.history.back()}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              fontSize: '16px',
              padding: '12px 24px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
          >
            Go Back
          </button>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              fontSize: '16px',
              padding: '12px 24px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );

  // Show not found page if campaign not found
  if (campaignNotFound) {
    return <NotFoundPage />;
  }

  return (
    <div style={{
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #e0f7fa 0%, #fce4ec 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      margin: 0,
      padding: '20px'
    }}>
      <div style={{
        background: '#ffffffee',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
        borderRadius: '15px',
        width: '450px',
        maxWidth: '90vw',
        padding: '35px',
        textAlign: 'center',
        border: '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <h2 style={{
          marginBottom: '25px',
          color: '#2c3e50',
          fontWeight: '700',
          letterSpacing: '1px'
        }}>
          Upload Settings File
        </h2>

        {isLoading && (
          <div style={{
            marginBottom: '20px',
            padding: '12px 20px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '8px',
            color: '#1976d2',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid #2196f3',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Downloading settings file...
          </div>
        )}

        <button
          onClick={handleConnect}
          disabled={connectDisabled}
          style={{
            backgroundColor: connectDisabled ? '#ccc' : '#848b92',
            color: 'white',
            fontSize: '16px',
            padding: '12px 25px',
            border: 'none',
            borderRadius: '8px',
            cursor: connectDisabled ? 'not-allowed' : 'pointer',
            width: '100%',
            marginBottom: '15px',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
            fontWeight: '600'
          }}
        >
          Connect Device
        </button>

        <span style={{
          fontSize: '14px',
          color: '#999',
          display: 'block',
          marginBottom: '20px'
        }}>
          {deviceStatus}
        </span>

        {/* Enhanced file status display */}
        {/* {zipBlob && (
          <div style={{
            margin: '15px 0',
            padding: '8px 16px',
            backgroundColor: zipBlob.size > 0 ? '#e8f5e8' : '#ffebee',
            border: `1px solid ${zipBlob.size > 0 ? '#4caf50' : '#f44336'}`,
            borderRadius: '6px',
            fontSize: '14px',
            color: zipBlob.size > 0 ? '#2e7d32' : '#c62828',
            fontWeight: '600'
          }}>
            {zipBlob.size > 0 
              ? `✅ ${fileType === 'json' ? 'JSON' : fileType === 'zip' ? 'ZIP' : 'File'} Cached (${Math.round(zipBlob.size / 1024)} KB) - Ready for Upload`
              : '❌ File is empty (0 KB)'
            }
          </div>
        )} */}

        {/* Information about single download */}
        {zipBlob && zipBlob.size > 0 && (
          <div style={{
            margin: '10px 0',
            padding: '6px 12px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#856404'
          }}>
            ℹ️ File downloaded once and cached. Upload will use this file.
          </div>
        )}

        {/* Show file details if available */}
        {zipBlob && zipBlob.size > 0 && fileType === 'json' && (
          <div style={{
            margin: '10px 0',
            padding: '6px 12px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#1976d2'
          }}>
            📄 Settings file (JSON format) - {zipBlob.size} bytes
          </div>
        )}

        {/* Show retry button if download failed or file is empty */}
        {downloadAttempted && (!zipBlob || zipBlob.size === 0) && (
          <button
            onClick={handleRetryDownload}
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#ccc' : '#ff9800',
              color: 'white',
              fontSize: '14px',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              marginBottom: '15px',
              fontWeight: '600'
            }}
          >
            🔄 Retry Download
          </button>
        )}

        <div style={{
          width: '100%',
          border: '1px solid #ddd',
          padding: '3px',
          borderRadius: '3px',
          marginBottom: '20px'
        }}>
          <div style={{
            height: '20px',
            backgroundColor: '#4CAF50',
            width: `${progress}%`,
            borderRadius: '2px',
            textAlign: 'center',
            lineHeight: '20px',
            color: 'white',
            fontSize: '12px'
          }}>
            {progress}%
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          marginTop: '25px',
          flexDirection: 'column'
        }}>
          <button
            onClick={handleUpload}
            disabled={uploadDisabled || !zipBlob || zipBlob.size === 0}
            style={{
              backgroundColor: (uploadDisabled || !zipBlob || zipBlob.size === 0) ? '#ccc' : '#848b92',
              color: 'white',
              fontSize: '16px',
              padding: '12px 25px',
              border: 'none',
              borderRadius: '8px',
              cursor: (uploadDisabled || !zipBlob || zipBlob.size === 0) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
              fontWeight: '600'
            }}
          >
            Upload
          </button>

                    {zipBlob && zipBlob.size > 0 && (
            <button
              onClick={handleManualDownload}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                fontSize: '16px',
                padding: '12px 25px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                fontWeight: '600'
              }}
            >
              Download ZIP
            </button>
          )}
        </div>

        {message && (
          <div style={{
            marginTop: '15px',
            fontSize: '15px',
            minHeight: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            color: messageType === 'success' ? '#2e7d32' : '#c62828'
          }}>
            <div style={{ fontSize: '18px' }}>
              {messageType === 'success' ? '✅' : '❌'}
            </div>
            <div>{message}</div>
          </div>
        )}

        <div style={{ marginTop: '20px' }}>
          <button
            type="button"
            onClick={() => setLogsVisible(!logsVisible)}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #6c757d',
              color: '#6c757d',
              fontSize: '12px',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {logsVisible ? "Hide Logs" : "Show Logs"}
          </button>
        </div>

        {logsVisible && (
          <div
            style={{
              marginTop: '20px',
              maxHeight: '150px',
              overflowY: 'auto',
              textAlign: 'left',
              fontSize: '13px',
              color: '#666',
              background: '#f8f8f8',
              padding: '10px',
              borderRadius: '6px'
            }}
            ref={logContainerRef}
          >
            {logs.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999' }}>No logs yet...</div>
            ) : (
              logs.map((log, index) => (
                <div key={index} style={{ marginBottom: '2px' }}>{log}</div>
              ))
            )}
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media (max-width: 500px) {
            .upload-container {
              width: 100%;
              padding: 25px;
            }
          }
        `
      }} />
    </div>
  );
};

export default HIDFileUploader;